/**
 * DOM ladder estimated P/L column (owner request 2026-09-27, Tradovate-style): with an open
 * position in the ladder's account + symbol, each price row shows the P&L the position would have
 * if price reached that row. Integer math only (`@helm/core/trading`): the average may be a
 * fraction of a tick after scaling in, and is kept as the exact cost Σ price×qty.
 *
 * Owner decisions: FLAT → the column is blank; units default to dollars and the column header
 * toggles dollars ↔ ticks (persisted as `dom_pnl_unit` in the synced settings document).
 */
import { avgRowTicks, openTickContracts, roundHalfAway, type ExactPosition } from "@helm/core/trading";
import { MINUS, formatCents, type PriceFormat, type Tone } from "./format.ts";

export type DomPnlUnit = "dollars" | "ticks";

/** The stored setting, defaulting anything unknown to dollars. */
export function domPnlUnitOf(v: unknown): DomPnlUnit {
  return v === "ticks" ? "ticks" : "dollars";
}

export type DomPnlCell = {
  text: string;
  /** Sign of the exact P&L (green > 0, red < 0, grey = 0). */
  tone: Exclude<Tone, "none">;
  /** This row holds the average entry price (nearest tick). */
  isAvg: boolean;
  /** Exact P&L at this row: cents (null when the tick value is unknown) and tick-contracts. */
  cents: bigint | null;
  tickContracts: bigint;
};

/**
 * Ticks per contract (`tick-contracts ÷ |qty|`), to one decimal, halves away from zero: `+8`,
 * `−0.5`, `+3.3`. Zero prints as `0`.
 */
export function formatPerContractTicks(tickContracts: bigint, netQty: number): string {
  const q = BigInt(Math.abs(netQty));
  if (q === 0n) return "";
  const tenths = roundHalfAway(tickContracts * 10n, q);
  if (tenths === 0n) return "0";
  const abs = tenths < 0n ? -tenths : tenths;
  const whole = abs / 10n;
  const frac = abs % 10n;
  const body = frac === 0n ? whole.toString() : `${whole.toString()}.${frac.toString()}`;
  return `${tenths < 0n ? MINUS : "+"}${body}`;
}

/**
 * **Display only:** an exact average `num/den` ticks as a price. On the tick grid it prints like
 * any price; a fractional average (scaled in) gets 2 more decimals, e.g. 85 801.5 ticks at 1/4 →
 * "21,450.3750". The float is used only to print; P&L never goes through it.
 */
export function formatAvgPrice(avg: { num: bigint; den: bigint } | null, f: PriceFormat | null): string {
  if (avg === null || f === null || f.tickSizeDen === 0 || avg.den === 0n) return "—";
  const whole = avg.num % avg.den === 0n;
  const decimals = f.displayDecimals + (whole ? 0 : 2);
  const price = (Number(avg.num) * f.tickSizeNum) / (Number(avg.den) * f.tickSizeDen);
  return price.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/**
 * The cell for one ladder row, or null (blank) when there is no position or it is flat.
 * `tickValueCents` ≤ 0 means unknown: dollars then show "—" (ticks still work).
 */
export function domPnlCell(
  pos: ExactPosition | null,
  rowTicks: number,
  tickValueCents: number,
  unit: DomPnlUnit,
): DomPnlCell | null {
  if (pos === null || pos.netQty === 0) return null;
  const tc = openTickContracts(pos, rowTicks);
  const known = Number.isSafeInteger(tickValueCents) && tickValueCents > 0;
  const cents = known ? tc * BigInt(tickValueCents) : null;
  const tone = tc > 0n ? "pos" : tc < 0n ? "neg" : "zero";
  let text: string;
  if (unit === "ticks") text = formatPerContractTicks(tc, pos.netQty);
  else if (cents === null) text = "—";
  else text = cents === 0n ? "$0" : formatCents(cents, { signed: true, trimZeroCents: true });
  return { text, tone, isAvg: avgRowTicks(pos) === BigInt(rowTicks), cents, tickContracts: tc };
}
